// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever talks to a server; the AI calls (examiner questions + per-part transcript
// feedback) live in brain.ts and send only the setup fields / the part's questions +
// the user's OWN transcript text.
//
// ⚠️ DO NOT write "voice never leaves the device". It is FALSE: iOS Web Speech dictation
// ships the audio to APPLE's servers. What is true — and all we ever claim — is that audio
// never touches an ORIORA server, and Oriora doesn't retain it.
//
// Data model:
//   session  { id, exam, mode, topic, created_at, updated_at,
//              turn_count, answered_count, band_low, band_high, avg_score }  (computed on read)
//   turn     { id, session_id, part, idx, prompt, hint, transcript, score,
//              answered_at, created_at }
//   feedback { id, session_id, part, feedback_json, band_low, band_high, created_at }

import { invoke } from "./tauri-web";

export type Exam = "ielts" | "toefl";
export type Mode = "mock" | "interview" | "listen_repeat";
export type Part = "p1" | "p2" | "p3" | "interview" | "repeat";

export interface Session {
  id: string;
  exam: Exam;
  mode: Mode;
  /** Optional practice topic (free text). "" when auto-picked. */
  topic: string;
  /** "" = the full 3-part mock; otherwise the single part this session practises. */
  focus_part: string;
  created_at: string;
  updated_at: string;
  /** Computed on read. */
  turn_count: number;
  answered_count: number;
  /** Session estimated band range — averaged over its feedbacks. null when none yet. */
  band_low: number | null;
  band_high: number | null;
  /** Average Listen & Repeat score (0..100). null when none. */
  avg_score: number | null;
}

export interface Turn {
  id: string;
  session_id: string;
  part: Part;
  /** 0-based order within the part. */
  idx: number;
  /** Examiner question / cue-card topic line / Listen & Repeat target sentence. */
  prompt: string;
  /** Cue-card bullets (newline-joined) for p2; "" otherwise. */
  hint: string;
  /** The user's own transcript (dictated, then hand-correctable). "" until answered. */
  transcript: string;
  /** Local Listen & Repeat similarity 0..100; null for AI-feedback parts. */
  score: number | null;
  answered_at: string | null;
  created_at: string;
}

export interface FeedbackRow {
  id: string;
  session_id: string;
  part: Part;
  /** Structured feedback JSON exactly as the brain returned it. */
  feedback_json: string;
  band_low: number;
  band_high: number;
  created_at: string;
}

export interface NewSession {
  exam: Exam;
  mode: Mode;
  topic?: string;
  /** "" / omitted = the full 3-part mock. "p1" | "p2" | "p3" = practise that part alone. */
  focus_part?: "" | "p1" | "p2" | "p3";
}

export interface AccentGoal {
  id: string;
  name: string;
  lang: string;
  target_date: string;
  created_at: string;
}

export interface ShadowAttempt {
  id: string;
  lang: string;
  drill_key: string;
  sentence: string;
  heard: string;
  local_score: number | null;
  ai_json: string;
  audio_ref: string;
  created_at: string;
}

export interface Benchmark {
  id: string;
  lang: string;
  passage: string;
  audio_ref: string;
  recorded_on: string;
}

export interface PracticePlan {
  lang: string;
  name: string;
  plan_json: string;
  created_at: string;
}

export interface TrendPoint {
  session_id: string;
  date: string;
  band_mid: number;
}

// The structured per-part feedback (mirrors the server's /feedback response shape).
// Stored verbatim in feedback_json; parse with parseFeedback().
export interface Criterion {
  /** Estimated band for this criterion (1..9, half steps). */
  band: number;
  /** One grounded observation about THIS transcript on this criterion. */
  note: string;
}

export interface SpeakingFeedback {
  fluency_coherence: Criterion;
  lexical_resource: Criterion;
  grammar: Criterion;
  /** Estimated overall band RANGE from the transcript — never a guaranteed score. */
  band_low: number;
  band_high: number;
  strengths: string[];
  improvements: string[];
}

/** Parse a stored feedback_json string back into SpeakingFeedback; null when empty/corrupt. */
export function parseFeedback(raw: string): SpeakingFeedback | null {
  if (!raw || !raw.trim()) return null;
  try {
    const j = JSON.parse(raw) as Partial<SpeakingFeedback>;
    const crit = (c: unknown): Criterion => ({
      band: Number((c as { band?: unknown })?.band ?? 0) || 0,
      note: String((c as { note?: unknown })?.note ?? ""),
    });
    return {
      fluency_coherence: crit(j.fluency_coherence),
      lexical_resource: crit(j.lexical_resource),
      grammar: crit(j.grammar),
      band_low: Number(j.band_low ?? 0) || 0,
      band_high: Number(j.band_high ?? 0) || 0,
      strengths: Array.isArray(j.strengths) ? j.strengths.map(String) : [],
      improvements: Array.isArray(j.improvements) ? j.improvements.map(String) : [],
    };
  } catch {
    return null;
  }
}

export const store = {
  listSessions: (opts?: { limit?: number }) =>
    invoke<Session[]>("list_sessions", { limit: opts?.limit ?? 50000 }),
  getSession: (id: string) => invoke<Session | null>("get_session", { id }),
  createSession: (input: NewSession) => invoke<Session>("create_session", { input }),
  deleteSession: (id: string) => invoke<void>("delete_session", { id }),
  setTopic: (id: string, topic: string) => invoke<void>("set_topic", { id, topic }),
  /** Bulk-insert one part's questions as they arrive from the brain. */
  addTurns: (sessionId: string, part: Part, prompts: { prompt: string; hint?: string }[]) =>
    invoke<Turn[]>("add_turns", { sessionId, part, prompts }),
  listTurns: (sessionId: string) => invoke<Turn[]>("list_turns", { sessionId }),
  recordTranscript: (turnId: string, transcript: string) =>
    invoke<void>("record_transcript", { turnId, transcript }),
  /** One Listen & Repeat attempt — score computed ON-DEVICE (listen-repeat.ts), no AI call. */
  recordRepeat: (sessionId: string, prompt: string, transcript: string, score: number) =>
    invoke<Turn>("record_repeat", { sessionId, prompt, transcript, score }),

  // ── Accent Lab ──
  createAccentGoal: (name: string, lang: string, targetDate: string) =>
    invoke<AccentGoal>("create_accent_goal", { name, lang, targetDate }),
  listAccentGoals: () => invoke<AccentGoal[]>("list_accent_goals"),
  deleteAccentGoal: (id: string) => invoke<void>("delete_accent_goal", { id }),
  saveShadowAttempt: (a: { lang: string; drillKey: string; sentence: string; heard: string; localScore: number | null; aiJson: string; audioRef: string }) =>
    invoke<ShadowAttempt>("save_shadow_attempt", a),
  listShadowDays: () => invoke<string[]>("list_shadow_days"),
  listShadowAttempts: (limit: number) => invoke<ShadowAttempt[]>("list_shadow_attempts", { limit }),
  saveBenchmark: (lang: string, passage: string, audioRef: string) =>
    invoke<Benchmark>("save_benchmark", { lang, passage, audioRef }),
  listBenchmarks: () => invoke<Benchmark[]>("list_benchmarks"),

  savePracticePlan: (lang: string, name: string, planJson: string) =>
    invoke<void>("save_practice_plan", { lang, name, planJson }),
  getPracticePlan: (lang: string) => invoke<PracticePlan | null>("get_practice_plan", { lang }),
  deletePracticePlan: (lang: string) => invoke<void>("delete_practice_plan", { lang }),
  addFeedback: (sessionId: string, part: Part, feedback: SpeakingFeedback) =>
    invoke<FeedbackRow>("add_feedback", {
      sessionId,
      part,
      feedbackJson: JSON.stringify(feedback),
      bandLow: feedback.band_low,
      bandHigh: feedback.band_high,
    }),
  listFeedbacks: (sessionId: string) => invoke<FeedbackRow[]>("list_feedbacks", { sessionId }),
  /** Every feedback ever, oldest first — the per-criterion board parses these. */
  listAllFeedbacks: () => invoke<FeedbackRow[]>("list_all_feedbacks"),
  /** Band trend across sessions (local progress aggregate — nothing leaves the device). */
  bandTrend: () => invoke<TrendPoint[]>("band_trend"),
};

// ── Pure policy / display helpers ──────────────────────────────────────────────

export const EXAM_LABELS: Record<Exam, string> = {
  ielts: "IELTS",
  toefl: "TOEFL",
};

export const MODE_LABELS: Record<Mode, string> = {
  mock: "Mock speaking test",
  interview: "Simulated interview",
  listen_repeat: "Listen & Repeat",
};

export const PART_LABELS: Record<Part, string> = {
  p1: "Part 1 — Interview",
  p2: "Part 2 — Cue card",
  p3: "Part 3 — Discussion",
  interview: "Interview",
  repeat: "Listen & Repeat",
};

/** Suggested IELTS-flavoured practice topics for the setup datalist; free text allowed. */
export const TOPIC_SUGGESTIONS = [
  "hometown", "work and career", "studies", "travel", "food", "technology",
  "environment", "health", "hobbies", "family", "music", "sport",
];

/** Format an estimated band range, e.g. "6.0–6.5" (single value when low == high). */
export function bandRange(low: number | null, high: number | null): string {
  if (low == null || high == null || low <= 0) return "—";
  const f = (v: number) => v.toFixed(1);
  return low === high ? f(low) : `${f(low)}–${f(high)}`;
}

/** Normalize a tag — lowercase, trim, collapse internal whitespace. */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Human "x days ago" for an ISO date/datetime, relative to local today. */
export function relativeDays(dateStr: string | null): string {
  if (!dateStr) return "never";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return "never";
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} wk ago`;
  if (days < 365) return `${Math.round(days / 30)} mo ago`;
  return `${Math.round(days / 365)} yr ago`;
}
